import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Matter from 'matter-js';
import { XMarkIcon, PlayIcon, ArrowPathIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { sendLivePlayMetadataUpdates } from '../utils/livePlayMetadata';

interface Participant {
  id: string;
  name: string;
  color: string;
}

interface MeepleState {
  participantId: string;
  x: number; // Percentage 0-100
  y: number; // Percentage 0-100
}

interface ObstacleBase {
  id: number;
  x: number; // Percentage 0-100
  y: number; // Percentage 0-100
  rotation: number; // degrees
  size: number; // percentage of field
}

interface PolygonObstacle extends ObstacleBase {
  kind: 'polygon';
  sides: 3 | 4 | 5;
  color: string;
}

interface SvgObstacle extends ObstacleBase {
  kind: 'svg';
  src: string;
  color: string;
}

interface CircleObstacle extends ObstacleBase {
  kind: 'circle';
  color: string;
}

type Obstacle = PolygonObstacle | SvgObstacle | CircleObstacle;

const COLORS = [
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

const PRELOAD_SVGS = [
  '/images/meeple.svg',
  '/images/drop-shape.svg',
  '/images/hexagon.svg',
  '/images/city.svg',
  '/images/ticket.svg',
  '/images/trophy.svg',
];

const MEEPLE_SIZE_PCT = 8;
const BALL_SIZE_PCT = 4;
const BALL_SPEED_PCT = 1.2; // percentage per frame at 60fps
const OBSTACLE_BASE_PX = 40; // fixed pixel size for obstacles
const OBSTACLE_MIN_DIST = 8; // minimum distance (%) from meeples and other obstacles

// Playing field boundaries (percentage of container)
const FIELD_LEFT = 4;
const FIELD_RIGHT = 96;
const FIELD_TOP = 14;
const FIELD_BOTTOM = 82;
const FIELD_CENTER_X = (FIELD_LEFT + FIELD_RIGHT) / 2;
const FIELD_CENTER_Y = (FIELD_TOP + FIELD_BOTTOM) / 2;
const SPAWN_RADIUS_PCT = 6;

function colorFilter(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
    }
  }
  return `brightness(0) saturate(100%) invert(${Math.round(l * 100)}%) sepia(100%) saturate(${Math.round(s * 500)}%) hue-rotate(${Math.round(h)}deg) `;
}

const MeepleIcon = ({ color, className }: { color: string; className?: string }) => (
  <img
    src="/images/meeple.svg"
    alt="meeple"
    className={className}
    draggable={false}
    style={{ filter: colorFilter(color) }}
  />
);

function polygonPoints(sides: number, size: number, rotation: number): string {
  const points: string[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = (rotation * Math.PI) / 180 + (2 * Math.PI * i) / sides - Math.PI / 2;
    points.push(`${size / 2 + (size / 2) * Math.cos(angle)},${size / 2 + (size / 2) * Math.sin(angle)}`);
  }
  return points.join(' ');
}

const _svgHullCache = new Map<string, Matter.Vector[]>();

/** Fetch an SVG, sample all <path>/<polygon>/<rect> elements, return convex hull normalized to -0.5..0.5 */
async function getSvgHullVertices(src: string): Promise<Matter.Vector[]> {
  const cached = _svgHullCache.get(src);
  if (cached) return cached;

  const resp = await fetch(src);
  const text = await resp.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'image/svg+xml');
  const svgEl = doc.querySelector('svg');
  if (!svgEl) return [];

  // Parse viewBox to get coordinate system
  const vb = svgEl.getAttribute('viewBox')?.split(/[\s,]+/).map(Number) ?? [0, 0, 100, 100];
  const [, , vbW, vbH] = vb;
  const maxDim = Math.max(vbW, vbH);

  // We need a real DOM SVG to use getTotalLength/getPointAtLength
  const svgNs = 'http://www.w3.org/2000/svg';
  const tempSvg = document.createElementNS(svgNs, 'svg');
  tempSvg.style.position = 'absolute';
  tempSvg.style.left = '-9999px';
  document.body.appendChild(tempSvg);

  const raw: Matter.Vector[] = [];

  // Sample all <path> elements
  const paths = doc.querySelectorAll('path');
  for (const p of paths) {
    const d = p.getAttribute('d');
    if (!d) continue;
    const tempPath = document.createElementNS(svgNs, 'path');
    tempPath.setAttribute('d', d);
    tempSvg.appendChild(tempPath);
    const totalLen = tempPath.getTotalLength();
    const samples = Math.max(30, Math.ceil(totalLen / 5));
    for (let i = 0; i < samples; i++) {
      const pt = tempPath.getPointAtLength((i / samples) * totalLen);
      raw.push(Matter.Vector.create(pt.x, pt.y));
    }
    tempSvg.removeChild(tempPath);
  }

  // Sample all <polygon> elements
  const polygons = doc.querySelectorAll('polygon');
  for (const poly of polygons) {
    const pts = poly.getAttribute('points');
    if (!pts) continue;
    const coords = pts.trim().split(/[\s,]+/).map(Number);
    for (let i = 0; i < coords.length - 1; i += 2) {
      raw.push(Matter.Vector.create(coords[i], coords[i + 1]));
    }
  }

  // Sample all <rect> elements
  const rects = doc.querySelectorAll('rect');
  for (const rect of rects) {
    const x = parseFloat(rect.getAttribute('x') ?? '0');
    const y = parseFloat(rect.getAttribute('y') ?? '0');
    const w = parseFloat(rect.getAttribute('width') ?? '0');
    const h = parseFloat(rect.getAttribute('height') ?? '0');
    raw.push(Matter.Vector.create(x, y));
    raw.push(Matter.Vector.create(x + w, y));
    raw.push(Matter.Vector.create(x + w, y + h));
    raw.push(Matter.Vector.create(x, y + h));
  }

  document.body.removeChild(tempSvg);

  if (raw.length < 3) return [];

  const hull = Matter.Vertices.hull(raw as unknown as Matter.Vertex[]);
  // Center around the hull's own centroid so the physics body aligns with the
  // CSS-rendered visual (which is centered in its container).
  const hcx = hull.reduce((s, v) => s + v.x, 0) / hull.length;
  const hcy = hull.reduce((s, v) => s + v.y, 0) / hull.length;
  const result = hull.map(v => Matter.Vector.create((v.x - hcx) / maxDim, (v.y - hcy) / maxDim));
  _svgHullCache.set(src, result);
  return result;
}

function meeplesCollide(a: MeepleState, b: MeepleState): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy) < MEEPLE_SIZE_PCT;
}

function hasAnyCollision(meeples: MeepleState[]): boolean {
  for (let i = 0; i < meeples.length; i++) {
    for (let j = i + 1; j < meeples.length; j++) {
      if (meeplesCollide(meeples[i], meeples[j])) return true;
    }
  }
  return false;
}

function meepleHasCollision(id: string, meeples: MeepleState[]): boolean {
  const meeple = meeples.find(m => m.participantId === id);
  if (!meeple) return false;
  return meeples.some(
    other => other.participantId !== id && meeplesCollide(meeple, other)
  );
}

export interface FirstPlayerMinigameLocationState {
  teams: { teamName: string; playerNames: string[] }[];
  livePlayId?: string;
}

const FirstPlayerMinigamePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as FirstPlayerMinigameLocationState | null;

  const teams = useMemo(() => state?.teams ?? [], [state?.teams]);
  const livePlayId = state?.livePlayId ?? '';

  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [placedMeeples, setPlacedMeeples] = useState<MeepleState[]>([]);
  const [activeTurnIndex, setActiveTurnIndex] = useState(0);
  const [phase, setPhase] = useState<'placement' | 'obstacles' | 'countdown' | 'ballPhase' | 'revealed'>('placement');
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [winningParticipantId, setWinningParticipantId] = useState<string | null>(null);
  const [orderedParticipants, setOrderedParticipants] = useState<Participant[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [collisionWarning, setCollisionWarning] = useState(false);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [countdownValue, setCountdownValue] = useState(3);
  const [eliminatedIds, setEliminatedIds] = useState<string[]>([]);
  const [ballPos, setBallPos] = useState<{ x: number; y: number }>({ x: FIELD_CENTER_X, y: FIELD_CENTER_Y });
  const [ballTrail, setBallTrail] = useState<{ x: number; y: number }[]>([]);
  const [hideOverlays, setHideOverlays] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const touchDragRef = useRef<{ fromIndex: number; listEl: HTMLElement | null }>({ fromIndex: -1, listEl: null });
  const placedMeeplesRef = useRef(placedMeeples);
  placedMeeplesRef.current = placedMeeples;
  const activeTurnIndexRef = useRef(activeTurnIndex);
  activeTurnIndexRef.current = activeTurnIndex;
  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  const draggingIdRef = useRef(draggingId);
  draggingIdRef.current = draggingId;

  const obstaclesRef = useRef<Obstacle[]>([]);
  obstaclesRef.current = obstacles;

  const matterRef = useRef<{
    engine: Matter.Engine;
    ball: Matter.Body;
    meepleMap: Map<string, Matter.Body>;
  } | null>(null);
  const animFrameRef = useRef<number>(0);
  const eliminatedIdsRef = useRef<string[]>([]);
  eliminatedIdsRef.current = eliminatedIds;

  // Preload all SVGs on mount (images + hull geometry)
  useEffect(() => {
    PRELOAD_SVGS.forEach(src => {
      const img = new Image();
      img.src = src;
      getSvgHullVertices(src);
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (matterRef.current) {
        Matter.World.clear(matterRef.current.engine.world, false);
        Matter.Engine.clear(matterRef.current.engine);
        matterRef.current = null;
      }
    };
  }, []);

  // Redirect back if no teams were passed
  useEffect(() => {
    if (!state?.teams?.length) {
      navigate(-1);
    }
  }, [state, navigate]);

  // Initialize participants from teams
  useEffect(() => {
    if (teams.length > 0) {
      setParticipants(
        teams.map((t, i) => ({
          id: t.teamName,
          name: t.teamName,
          color: COLORS[i % COLORS.length],
        }))
      );
    }
  }, [teams]);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const handleStartGame = useCallback(async () => {
    if (orderedParticipants.length === 0) {
      navigate(-1);
      return;
    }
    if (livePlayId) {
      setIsSaving(true);
      try {
        const metadata: Record<string, Record<string, number>> = {};
        orderedParticipants.forEach((p, index) => {
          metadata[p.name] = { startingPosition: index + 1 };
        });
        await sendLivePlayMetadataUpdates(livePlayId, metadata);
      } catch (err) {
        console.error('Failed to save turn order:', err);
      }
    }
    navigate(-1);
  }, [livePlayId, orderedParticipants, navigate]);

  const clampPosition = (xPct: number, yPct: number) => ({
    x: Math.max(FIELD_LEFT + 2, Math.min(FIELD_RIGHT - 2, xPct)),
    y: Math.max(FIELD_TOP + 2, Math.min(FIELD_BOTTOM - 2, yPct)),
  });

  const pctFromEvent = (e: React.PointerEvent | PointerEvent) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    return {
      xPct: ((e.clientX - rect.left) / rect.width) * 100,
      yPct: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const startBallPhase = useCallback(async (parts: Participant[]) => {
    // Guard against double invocation (React Strict Mode)
    if (matterRef.current) return;

    setPhase('ballPhase');
    setBallPos({ x: FIELD_CENTER_X, y: FIELD_CENTER_Y });
    setEliminatedIds([]);
    eliminatedIdsRef.current = [];
    setBallTrail([{ x: FIELD_CENTER_X, y: FIELD_CENTER_Y }]);

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const W = rect.width;
    const H = rect.height;
    const pctToPxX = (pct: number) => (pct / 100) * W;
    const pctToPxY = (pct: number) => (pct / 100) * H;
    const pxToPctX = (px: number) => (px / W) * 100;
    const pxToPctY = (px: number) => (px / H) * 100;

    // Create engine with no gravity
    const engine = Matter.Engine.create({ gravity: { x: 0, y: 0 } });
    const world = engine.world;

    const wallT = 20;
    const fieldLPx = pctToPxX(FIELD_LEFT);
    const fieldRPx = pctToPxX(FIELD_RIGHT);
    const fieldTPx = pctToPxY(FIELD_TOP);
    const fieldBPx = pctToPxY(FIELD_BOTTOM);
    const fieldW = fieldRPx - fieldLPx;
    const fieldH = fieldBPx - fieldTPx;
    const midX = (fieldLPx + fieldRPx) / 2;
    const midY = (fieldTPx + fieldBPx) / 2;

    const wallOpts: Matter.IChamferableBodyDefinition = { isStatic: true, restitution: 1.05, friction: 0, label: 'wall' };
    const cornerR = 10; // small corner rounding radius
    const walls = [
      Matter.Bodies.rectangle(midX, fieldTPx - wallT / 2, fieldW - cornerR * 2, wallT, wallOpts),
      Matter.Bodies.rectangle(midX, fieldBPx + wallT / 2, fieldW - cornerR * 2, wallT, wallOpts),
      Matter.Bodies.rectangle(fieldLPx - wallT / 2, midY, wallT, fieldH - cornerR * 2, wallOpts),
      Matter.Bodies.rectangle(fieldRPx + wallT / 2, midY, wallT, fieldH - cornerR * 2, wallOpts),
    ];
    // Small corner circles flush with the wall edges to round off the 90° join
    const corners = [
      Matter.Bodies.circle(fieldLPx + cornerR * 0.4, fieldTPx + cornerR * 0.4, cornerR, wallOpts),
      Matter.Bodies.circle(fieldRPx - cornerR * 0.4, fieldTPx + cornerR * 0.4, cornerR, wallOpts),
      Matter.Bodies.circle(fieldRPx - cornerR * 0.4, fieldBPx - cornerR * 0.4, cornerR, wallOpts),
      Matter.Bodies.circle(fieldLPx + cornerR * 0.4, fieldBPx - cornerR * 0.4, cornerR, wallOpts),
    ];
    Matter.Composite.add(world, [...walls, ...corners]);

    // Add meeple bodies using convex hull of actual SVG shape
    const meepleMap = new Map<string, Matter.Body>();
    const meeples = placedMeeplesRef.current;
    const meepleSizePx = 48; // visual size of rendered meeple
    const meepleHull = await getSvgHullVertices('/images/meeple.svg');
    for (const m of meeples) {
      const scaledVerts = meepleHull.map(v =>
        Matter.Vector.create(v.x * meepleSizePx, v.y * meepleSizePx)
      );
      const body = Matter.Bodies.fromVertices(pctToPxX(m.x), pctToPxY(m.y), [scaledVerts], {
        isStatic: true,
        restitution: 1.05,
        label: m.participantId,
      });
      meepleMap.set(m.participantId, body);
      Matter.Composite.add(world, body);
    }

    // Add ball — DOM ball is BALL_SIZE_PCT vmin
    const vmin = Math.min(W, H);

    // Add obstacle bodies — vertices are pre-rotated to match the visual CSS rotation
    const currentObstacles = obstaclesRef.current;
    const obstacleOpts: Matter.IChamferableBodyDefinition = { isStatic: true, restitution: 1.05, friction: 0, label: 'obstacle' };

    const rotateVerts = (verts: Matter.Vector[], angleDeg: number): Matter.Vector[] => {
      const rad = (angleDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      return verts.map(v => Matter.Vector.create(v.x * cos - v.y * sin, v.x * sin + v.y * cos));
    };

    for (const obs of currentObstacles) {
      const sizePx = obs.size;
      let body: Matter.Body;
      if (obs.kind === 'polygon') {
        // Build polygon vertices manually and pre-rotate
        const polyVerts: Matter.Vector[] = [];
        for (let i = 0; i < obs.sides; i++) {
          const a = (2 * Math.PI * i) / obs.sides - Math.PI / 2;
          polyVerts.push(Matter.Vector.create(Math.cos(a) * sizePx / 2, Math.sin(a) * sizePx / 2));
        }
        const rotated = rotateVerts(polyVerts, obs.rotation);
        body = Matter.Bodies.fromVertices(pctToPxX(obs.x), pctToPxY(obs.y), [rotated], obstacleOpts);
      } else if (obs.kind === 'circle') {
        body = Matter.Bodies.circle(pctToPxX(obs.x), pctToPxY(obs.y), sizePx / 2, obstacleOpts);
      } else {
        // SVG obstacles — use convex hull from SVG geometry, pre-rotated
        const hull = await getSvgHullVertices(obs.src);
        if (hull.length >= 3) {
          const scaledVerts = hull.map(v =>
            Matter.Vector.create(v.x * sizePx, v.y * sizePx)
          );
          const rotated = rotateVerts(scaledVerts, obs.rotation);
          body = Matter.Bodies.fromVertices(pctToPxX(obs.x), pctToPxY(obs.y), [rotated], obstacleOpts);
        } else {
          body = Matter.Bodies.circle(pctToPxX(obs.x), pctToPxY(obs.y), sizePx / 2, obstacleOpts);
        }
      }
      Matter.Composite.add(world, body);
    }

    const ballSizePx = vmin * (BALL_SIZE_PCT / 100);
    const ball = Matter.Bodies.circle(pctToPxX(FIELD_CENTER_X), pctToPxY(FIELD_CENTER_Y), ballSizePx / 2, {
      restitution: 1.05,
      friction: 0,
      frictionAir: 0,
      frictionStatic: 0,
      inertia: Infinity,
      inverseInertia: 0,
      label: 'ball',
    });

    const angle = Math.random() * 2 * Math.PI;
    const speedPx = (BALL_SPEED_PCT / 100) * vmin;
    Matter.Body.setVelocity(ball, { x: Math.cos(angle) * speedPx, y: Math.sin(angle) * speedPx });
    Matter.Composite.add(world, ball);

    matterRef.current = { engine, ball, meepleMap };

    // Collision handling — only track meeple eliminations, let Matter.js handle bouncing
    Matter.Events.on(engine, 'collisionStart', (event) => {
      for (const pair of event.pairs) {
        const bodies = [pair.bodyA, pair.bodyB];
        const ballBody = bodies.find(b => b.label === 'ball');
        const other = bodies.find(b => b !== ballBody);
        if (!ballBody || !other) continue;

        // 10% chance on any bounce: nudge the ball in a random orthogonal direction
        if (Math.random() < 0.1) {
          const vel = ballBody.velocity;
          const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
          if (speed > 0.01) {
            // Orthogonal unit vector (perpendicular to current velocity)
            const sign = Math.random() > 0.5 ? 1 : -1;
            const orthoX = (-vel.y / speed) * sign;
            const orthoY = (vel.x / speed) * sign;
            // Add 20% of speed as orthogonal nudge, then re-normalize
            const nudge = 0.2;
            const newVx = vel.x + orthoX * speed * nudge;
            const newVy = vel.y + orthoY * speed * nudge;
            const newSpeed = Math.sqrt(newVx * newVx + newVy * newVy);
            const s = speed / newSpeed;
            Matter.Body.setVelocity(ballBody, { x: newVx * s, y: newVy * s });
          }
        }

        if (other.label === 'wall' || other.label === 'obstacle') {
          // wall/obstacle — ball bounces naturally
        } else {
          // Meeple hit
          const hitId = other.label;
          const currentEliminated = eliminatedIdsRef.current;
          if (currentEliminated.includes(hitId)) continue;

          const newEliminated = [...currentEliminated, hitId];
          eliminatedIdsRef.current = newEliminated;
          setEliminatedIds(newEliminated);

          // Remove meeple body from world
          Matter.Composite.remove(world, other);
          matterRef.current?.meepleMap.delete(hitId);

          // Check if only 1 remains
          const remainingMeeples = meeples.filter(me => !newEliminated.includes(me.participantId));
          if (remainingMeeples.length <= 1) {
            const winnerId = remainingMeeples[0]?.participantId ?? hitId;
            setWinningParticipantId(winnerId);

            // Build clockwise order from winner
            const meeplesWithAngles = meeples.map(m => {
              const dx = m.x - 50;
              const dy = m.y - 50;
              const a = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
              return { ...m, angle: a };
            });
            meeplesWithAngles.sort((a, b) => a.angle - b.angle);

            const winnerIdx = meeplesWithAngles.findIndex(m => m.participantId === winnerId);
            const orderedIds: string[] = [];
            if (winnerIdx !== -1) {
              orderedIds.push(...meeplesWithAngles.slice(winnerIdx).map(m => m.participantId));
              orderedIds.push(...meeplesWithAngles.slice(0, winnerIdx).map(m => m.participantId));
            }

            const ordered = orderedIds
              .map(id => parts.find(p => p.id === id))
              .filter((p): p is Participant => !!p);
            setOrderedParticipants(ordered);
            setTimeout(() => setPhase('revealed'), 600);
          }
        }
      }
    });

    // Animation loop
    const trail: { x: number; y: number }[] = [{ x: FIELD_CENTER_X, y: FIELD_CENTER_Y }];
    let frameCount = 0;
    let gameOver = false;

    const tick = () => {
      if (gameOver) return;

      // Enforce constant speed before physics step to prevent drift
      const velBefore = ball.velocity;
      const speedBefore = Math.sqrt(velBefore.x * velBefore.x + velBefore.y * velBefore.y);
      if (speedBefore > 0.01) {
        const s = speedPx / speedBefore;
        Matter.Body.setVelocity(ball, { x: velBefore.x * s, y: velBefore.y * s });
      }

      Matter.Engine.update(engine, 1000 / 60);

      const bx = pxToPctX(ball.position.x);
      const by = pxToPctY(ball.position.y);
      setBallPos({ x: bx, y: by });

      frameCount++;
      if (frameCount % 3 === 0) {
        trail.push({ x: bx, y: by });
        setBallTrail([...trail]);
      }

      // Check if game ended (only 1 or 0 meeples left)
      const remaining = meeples.filter(me => !eliminatedIdsRef.current.includes(me.participantId));
      if (remaining.length <= 1) {
        gameOver = true;
        return;
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const startCountdownPhase = useCallback((parts: Participant[], meeples: MeepleState[]) => {
    void meeples; // meeples already placed in state
    setPhase('countdown');
    setCountdownValue(3);

    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count <= 0) {
        clearInterval(interval);
        startBallPhase(parts);
      } else {
        setCountdownValue(count);
      }
    }, 1000);
  }, [startBallPhase]);

  const startObstaclePhase = useCallback((parts: Participant[], meeples: MeepleState[]) => {
    setPhase('obstacles');
    setObstacles([]);

    // Deterministic obstacles placed at random positions, rotations pre-determined
    const cityColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    const randRot = () => Math.floor(Math.random() * 360);
    const fixedObstacles: Array<Omit<SvgObstacle, 'id' | 'x' | 'y'> | Omit<CircleObstacle, 'id' | 'x' | 'y'> | Omit<PolygonObstacle, 'id' | 'x' | 'y'>> = [
      { kind: 'svg', src: '/images/drop-shape.svg', color: '#38BDF8', rotation: randRot(), size: OBSTACLE_BASE_PX * 1.2 },
      { kind: 'svg', src: '/images/hexagon.svg', color: '#FF4500', rotation: randRot(), size: OBSTACLE_BASE_PX * 1.5 },
      { kind: 'circle', color: '#D1D5DB', rotation: 0, size: OBSTACLE_BASE_PX * 1.1 },
      { kind: 'svg', src: '/images/city.svg', color: cityColor, rotation: randRot(), size: OBSTACLE_BASE_PX * 2 },
      { kind: 'polygon', sides: 4, color: '#CD7F32', rotation: randRot(), size: OBSTACLE_BASE_PX * 0.5 },
      { kind: 'polygon', sides: 4, color: '#C0C0C0', rotation: randRot(), size: OBSTACLE_BASE_PX * 0.75 },
      { kind: 'polygon', sides: 4, color: '#FFD700', rotation: randRot(), size: OBSTACLE_BASE_PX * 1.1 },
      { kind: 'svg', src: '/images/ticket.svg', color: COLORS[Math.floor(Math.random() * COLORS.length)], rotation: randRot(), size: OBSTACLE_BASE_PX * 1.3 },
      { kind: 'svg', src: '/images/trophy.svg', color: COLORS[Math.floor(Math.random() * COLORS.length)], rotation: randRot(), size: OBSTACLE_BASE_PX * 1.2 },
    ];

    const placed: Obstacle[] = [];
    let fixedIndex = 0;

    const findValidPosition = (): { x: number; y: number } => {
      for (let attempt = 0; attempt < 50; attempt++) {
        const x = FIELD_LEFT + 4 + Math.random() * (FIELD_RIGHT - FIELD_LEFT - 8);
        const y = FIELD_TOP + 4 + Math.random() * (FIELD_BOTTOM - FIELD_TOP - 8);

        const tooCloseToMeeple = meeples.some(m => {
          const dx = m.x - x, dy = m.y - y;
          return Math.sqrt(dx * dx + dy * dy) < OBSTACLE_MIN_DIST;
        });
        if (tooCloseToMeeple) continue;

        const dxC = FIELD_CENTER_X - x, dyC = FIELD_CENTER_Y - y;
        if (Math.sqrt(dxC * dxC + dyC * dyC) < SPAWN_RADIUS_PCT + 2) continue;

        const tooCloseToObstacle = placed.some(o => {
          const dx = o.x - x, dy = o.y - y;
          return Math.sqrt(dx * dx + dy * dy) < OBSTACLE_MIN_DIST;
        });
        if (tooCloseToObstacle) continue;

        return { x, y };
      }
      // Fallback
      return {
        x: FIELD_LEFT + 4 + Math.random() * (FIELD_RIGHT - FIELD_LEFT - 8),
        y: FIELD_TOP + 4 + Math.random() * (FIELD_BOTTOM - FIELD_TOP - 8),
      };
    };

    const interval = setInterval(() => {
      if (fixedIndex >= fixedObstacles.length) {
        clearInterval(interval);
        startCountdownPhase(parts, meeples);
        return;
      }

      const pos = findValidPosition();
      const obs = fixedObstacles[fixedIndex];
      placed.push({ ...obs, id: fixedIndex, x: pos.x, y: pos.y } as Obstacle);
      setObstacles([...placed]);
      fixedIndex++;
    }, 400);
  }, [startCountdownPhase]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (phase !== 'placement') return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);

    const pos = pctFromEvent(e);
    if (!pos) return;

    const meeples = placedMeeplesRef.current;
    const parts = participantsRef.current;

    // Check if clicking near any already-placed meeple to pick it up
    const hitMeeple = meeples.find(m => {
      const dx = m.x - pos.xPct;
      const dy = m.y - pos.yPct;
      return Math.sqrt(dx * dx + dy * dy) < MEEPLE_SIZE_PCT;
    });

    if (hitMeeple) {
      // Switch active turn to this meeple's owner so they can reposition
      const ownerIndex = parts.findIndex(p => p.id === hitMeeple.participantId);
      if (ownerIndex !== -1) {
        setActiveTurnIndex(ownerIndex);
      }
      setDraggingId(hitMeeple.participantId);
      setCollisionWarning(false);
      return;
    }

    // No meeple hit — place a new one for the active player if they haven't placed yet
    const activeParticipant = parts[activeTurnIndexRef.current];
    if (!activeParticipant) return;

    const alreadyPlaced = meeples.some(m => m.participantId === activeParticipant.id);
    if (!alreadyPlaced) {
      const clamped = clampPosition(pos.xPct, pos.yPct);
      setPlacedMeeples(prev => [
        ...prev,
        { participantId: activeParticipant.id, x: clamped.x, y: clamped.y },
      ]);
      setDraggingId(activeParticipant.id);
      setCollisionWarning(false);
    }
  }, [phase]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const currentDragging = draggingIdRef.current;
    if (!currentDragging) return;
    e.preventDefault();

    const pos = pctFromEvent(e);
    if (!pos) return;
    const clamped = clampPosition(pos.xPct, pos.yPct);

    setPlacedMeeples(prev =>
      prev.map(m =>
        m.participantId === currentDragging ? { ...m, x: clamped.x, y: clamped.y } : m
      )
    );
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const currentDragging = draggingIdRef.current;
    if (!currentDragging) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);

    setDraggingId(null);

    setPlacedMeeples(currentMeeples => {
      if (meepleHasCollision(currentDragging, currentMeeples)) {
        setCollisionWarning(true);
        return currentMeeples;
      }

      setCollisionWarning(false);
      const currentIndex = activeTurnIndexRef.current;
      const parts = participantsRef.current;

      if (currentIndex < parts.length - 1) {
        setActiveTurnIndex(currentIndex + 1);
      } else {
        if (currentMeeples.length === parts.length && !hasAnyCollision(currentMeeples)) {
          startObstaclePhase(parts, currentMeeples);
        }
      }
      return currentMeeples;
    });
  }, [startObstaclePhase]);

  const handleReset = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (matterRef.current) {
      Matter.World.clear(matterRef.current.engine.world, false);
      Matter.Engine.clear(matterRef.current.engine);
      matterRef.current = null;
    }
    setPlacedMeeples([]);
    setObstacles([]);
    setActiveTurnIndex(0);
    setPhase('placement');
    setWinningParticipantId(null);
    setOrderedParticipants([]);
    setCollisionWarning(false);
    setEliminatedIds([]);
    eliminatedIdsRef.current = [];
    setBallPos({ x: FIELD_CENTER_X, y: FIELD_CENTER_Y });
    setBallTrail([]);
    setCountdownValue(3);
    setHideOverlays(false);
  }, []);

  // --- Drag & drop reorder handlers (HTML5 drag for desktop) ---
  const reorder = useCallback((from: number, to: number) => {
    if (from === to) return;
    setOrderedParticipants(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const handleDragStart = useCallback((_e: React.DragEvent, index: number) => {
    setDragFromIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragFromIndex !== null && dragOverIndex !== null) {
      reorder(dragFromIndex, dragOverIndex);
    }
    setDragFromIndex(null);
    setDragOverIndex(null);
  }, [dragFromIndex, dragOverIndex, reorder]);

  // --- Touch-based reorder for mobile ---
  const handleTouchStart = useCallback((e: React.TouchEvent, index: number) => {
    touchDragRef.current.fromIndex = index;
    touchDragRef.current.listEl = (e.currentTarget as HTMLElement).parentElement;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const listEl = touchDragRef.current.listEl;
    if (!listEl) return;
    const children = Array.from(listEl.children) as HTMLElement[];
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        setDragOverIndex(i);
        return;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const from = touchDragRef.current.fromIndex;
    if (from >= 0 && dragOverIndex !== null) {
      reorder(from, dragOverIndex);
    }
    touchDragRef.current.fromIndex = -1;
    setDragOverIndex(null);
  }, [dragOverIndex, reorder]);

  if (!teams.length) return null;

  const activeParticipant = participants[activeTurnIndex];
  const currentHasPlaced = activeParticipant
    ? placedMeeples.some(m => m.participantId === activeParticipant.id)
    : false;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col items-center justify-center">
      <style>{`
        @keyframes obstacle-pop {
          0% { transform: scale(0); opacity: 0; }
          70% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {/* Header / Instructions */}
      <div className="absolute top-4 left-0 right-0 z-10 text-center pointer-events-none">
        {phase === 'placement' && activeParticipant && (
          <div className="flex flex-col items-center gap-2">
            <div className="bg-amber-50/90 backdrop-blur px-6 py-3 rounded-full inline-block shadow-lg">
              <span className="text-xl font-bold font-serif" style={{ color: activeParticipant.color }}>
                {activeParticipant.name}
              </span>
              <span className="text-stone-800 font-medium ml-2">
                {currentHasPlaced ? 'drag to reposition, then release' : 'tap to place your Meeple!'}
              </span>
            </div>
            {collisionWarning && (
              <div className="bg-red-500/90 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-pulse">
                Meeples are overlapping — drag yours to a free spot
              </div>
            )}
          </div>
        )}
        {phase === 'obstacles' && (
          <div className="text-white text-2xl font-bold font-serif drop-shadow-lg animate-pulse">
            Placing obstacles...
          </div>
        )}
        {phase === 'revealed' && winningParticipantId && !hideOverlays && (
          <div className="flex flex-col items-center">
            <div className="text-white text-2xl font-medium font-serif mb-2">First Player:</div>
            <div className="bg-amber-50 px-8 py-4 rounded-2xl shadow-xl border border-stone-200">
              <span
                className="text-4xl font-black font-serif"
                style={{ color: participants.find(p => p.id === winningParticipantId)?.color }}
              >
                {participants.find(p => p.id === winningParticipantId)?.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={goBack}
        className="absolute top-4 right-4 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
      >
        <XMarkIcon className="h-8 w-8" />
      </button>

      {/* Reset Button (during placement and ball phase) */}
      {((phase === 'placement' && placedMeeples.length > 0) || phase === 'ballPhase') && (
        <button
          onClick={handleReset}
          className="absolute top-4 right-16 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
          title="Reset all placements"
        >
          <ArrowPathIcon className="h-8 w-8" />
        </button>
      )}

      {/* Countdown Overlay */}
      {phase === 'countdown' && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div
            key={countdownValue}
            className="text-white font-black animate-ping"
            style={{ fontSize: '12rem', lineHeight: 1, animationDuration: '0.8s', animationIterationCount: 1 }}
          >
            {countdownValue}
          </div>
        </div>
      )}

      {/* Game Area */}
      <div
        ref={containerRef}
        className="w-full h-full cursor-pointer relative overflow-hidden touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Playing Field Border */}
        <div
          style={{
            position: 'absolute',
            left: `${FIELD_LEFT}%`,
            top: `${FIELD_TOP}%`,
            width: `${FIELD_RIGHT - FIELD_LEFT}%`,
            height: `${FIELD_BOTTOM - FIELD_TOP}%`,
            border: '2px solid rgba(255,255,255,0.2)',
            borderRadius: '1.5rem',
            pointerEvents: 'none',
            zIndex: 0,
            backgroundImage: 'url(/images/fp-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.5,
          }}
        />

        {/* Spawn Circle */}
        {phase !== 'revealed' && (
          <div
            style={{
              position: 'absolute',
              left: `${FIELD_CENTER_X}%`,
              top: `${FIELD_CENTER_Y}%`,
              width: `${SPAWN_RADIUS_PCT * 2}vmin`,
              height: `${SPAWN_RADIUS_PCT * 2}vmin`,
              transform: 'translate(-50%, -50%)',
              border: '2px dashed rgba(239,68,68,0.4)',
              borderRadius: '50%',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        {/* Obstacles */}
        {(phase === 'obstacles' || phase === 'countdown' || phase === 'ballPhase' || phase === 'revealed') &&
          obstacles.map(obs => {
            const sizePx = `${obs.size}px`;
            // Outer div: positions and rotates (stable transform, no animation)
            const outerStyle: React.CSSProperties = {
              position: 'absolute',
              left: `${obs.x}%`,
              top: `${obs.y}%`,
              width: sizePx,
              height: sizePx,
              transform: `translate(-50%, -50%) rotate(${obs.rotation}deg)`,
              zIndex: 5,
              pointerEvents: 'none',
              filter: 'drop-shadow(0px 6px 10px rgba(0,0,0,0.6)) drop-shadow(0px 2px 4px rgba(0,0,0,0.4))',
            };
            // Inner style: pop animation only (scale + opacity)
            const popStyle: React.CSSProperties = {
              width: '100%',
              height: '100%',
              animation: 'obstacle-pop 0.3s ease-out',
            };

            if (obs.kind === 'svg') {
              return (
                <div key={obs.id} style={outerStyle}>
                  <div style={popStyle}>
                    <img
                      src={obs.src}
                      alt="obstacle"
                      draggable={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        filter: colorFilter(obs.color),
                      }}
                    />
                  </div>
                </div>
              );
            }

            if (obs.kind === 'circle') {
              return (
                <div key={obs.id} style={outerStyle}>
                  <div style={{
                    ...popStyle,
                    borderRadius: '50%',
                    backgroundColor: obs.color,
                    border: `2px solid ${obs.color}`,
                  }} />
                </div>
              );
            }

            // polygon
            return (
              <div key={obs.id} style={outerStyle}>
                <div style={popStyle}>
                  <svg width="100%" height="100%" viewBox="0 0 100 100">
                    <polygon
                      points={polygonPoints(obs.sides, 100, 0)}
                      fill={obs.color}
                      stroke={obs.color}
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>
            );
          })
        }

        {/* Ball Trail */}
        {(phase === 'ballPhase' || phase === 'revealed') && ballTrail.length > 1 && (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 15, pointerEvents: 'none' }}
          >
            <polyline
              points={ballTrail.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth="0.3"
              strokeDasharray="0.8 1.2"
              strokeLinecap="round"
            />
          </svg>
        )}

        {/* Ball */}
        {(phase === 'ballPhase' || phase === 'revealed') && (
          <div
            style={{
              position: 'absolute',
              left: `${ballPos.x}%`,
              top: `${ballPos.y}%`,
              transform: 'translate(-50%, -50%)',
              width: `${BALL_SIZE_PCT}vmin`,
              height: `${BALL_SIZE_PCT}vmin`,
              borderRadius: '50%',
              backgroundColor: 'white',
              boxShadow: '0 0 12px rgba(255,255,255,0.8), 0 0 24px rgba(255,255,255,0.4)',
              zIndex: 20,
            }}
          />
        )}

        {placedMeeples.map(meeple => {
          const p = participants.find(part => part.id === meeple.participantId);
          if (!p) return null;

          const isEliminated = eliminatedIds.includes(meeple.participantId);
          const isWinner = phase === 'revealed' && meeple.participantId === winningParticipantId;
          const isDimmedRevealed = phase === 'revealed' && !isWinner;

          return (
            <div
              key={meeple.participantId}
              style={{
                position: 'absolute',
                left: `${meeple.x}%`,
                top: `${meeple.y}%`,
                transform: `translate(-50%, -50%) ${isWinner ? 'scale(1.3)' : ''}`,
                opacity: isEliminated ? 0.25 : isDimmedRevealed ? 0.3 : 1,
                transition: 'transform 0.15s ease, opacity 0.5s ease',
                zIndex: isWinner ? 10 : 1,
                filter: 'drop-shadow(0px 6px 10px rgba(0,0,0,0.6)) drop-shadow(0px 2px 4px rgba(0,0,0,0.4))',
              }}
            >
              <MeepleIcon
                color={p.color}
                className="w-16 h-16"
              />
              {isEliminated && phase === 'ballPhase' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <XMarkIcon className="h-10 w-10 text-red-500 drop-shadow-lg" />
                </div>
              )}
              {phase === 'placement' &&
                meeple.participantId === activeParticipant?.id &&
                collisionWarning && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full border-4 border-red-500 animate-ping opacity-50" />
                </div>
              )}
              {/* Name Label */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap bg-stone-900/60 text-white text-xs px-2 py-0.5 rounded pointer-events-none">
                {p.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Toggle to hide overlays and inspect ball trace */}
      {phase === 'revealed' && (
        <button
          onClick={() => setHideOverlays(h => !h)}
          className="absolute top-4 left-4 z-40 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors text-xs font-mono"
          title="Toggle overlays"
        >
          {hideOverlays ? 'Show UI' : 'Hide UI'}
        </button>
      )}

      {/* Bottom Panel: Turn Order + Buttons */}
      {phase === 'revealed' && orderedParticipants.length > 0 && !hideOverlays && (
        <div className="absolute bottom-0 left-0 right-0 z-30 flex flex-col items-center pb-24 pt-4 bg-gradient-to-t from-stone-900 via-stone-900/95 to-transparent pointer-events-auto">
          <div className="bg-stone-800/90 backdrop-blur-md rounded-xl p-3 max-w-sm w-full border border-stone-700 shadow-2xl mb-4 mx-4">
            <div className="text-stone-300 text-xs font-bold mb-2 text-center uppercase tracking-widest">
              Turn Order — drag to adjust
            </div>
            <div className="space-y-1.5">
              {orderedParticipants.map((p, index) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className={`flex items-center rounded-lg p-2 border cursor-grab active:cursor-grabbing select-none transition-colors ${
                    dragOverIndex === index
                      ? 'bg-primary-900/50 border-primary-500'
                      : 'bg-stone-800/50 border-stone-700/50'
                  }`}
                >
                  <Bars3Icon className="h-4 w-4 text-stone-500 mr-2 flex-shrink-0" />
                  <div className="w-5 h-5 rounded-full flex items-center justify-center bg-stone-700 text-white text-xs font-bold mr-2 flex-shrink-0 shadow-inner">
                    {index + 1}
                  </div>
                  <MeepleIcon color={p.color} className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{p.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-2xl transition hover:scale-105 active:scale-95"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Redo
            </button>
            <button
              onClick={handleStartGame}
              disabled={isSaving}
              className="flex items-center gap-3 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-2xl font-bold text-lg shadow-2xl transition hover:scale-105 active:scale-95 disabled:scale-100"
            >
              <PlayIcon className="h-6 w-6" />
              {isSaving ? 'Saving...' : livePlayId ? 'Start Game' : 'Done'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirstPlayerMinigamePage;
